import { useState, useEffect } from "react";
import {
  InsightsCard,
  InsightsDividerList,
  InsightsDividerRow,
  MaskedAccountNumber,
  formatDate,
  formatCurrency,
} from "./InsightsCard";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { decryptPersonData } from "@/shared/utils/serverEncryption";
import { useCitation, getCitationForField } from "./CitationContext";

interface Holding {
  cusip_number?: string;
  company_name?: string;
  ticker?: string;
  transfer_agent?: string;
  shares?: number;
  cost_basis?: number;
  market_value?: number;
}

interface Account {
  account_number?: string;
  account_type?: string;
  total_value?: number;
  holdings?: Holding[];
  joint_account_holders?: string[];
  tod_beneficiaries?: string[];
}

export interface InvestmentAccountStatementExtraction {
  institution_name?: string;
  statement_date?: string;
  accounts?: Account[];
}

export default function InvestmentAccountStatementInsights({
  data,
  citations,
}: {
  data: InvestmentAccountStatementExtraction;
  citations?: Record<string, unknown> | null;
}) {
  const { showCitation } = useCitation();

  const handleFieldClick = (fieldPath: string) => {
    const citation = getCitationForField(citations, fieldPath);
    if (citation) showCitation(citation);
  };

  const { institution_name, statement_date, accounts } = data;
  const { person } = useAppContext();

  const [decryptedAccountNumbers, setDecryptedAccountNumbers] = useState<
    Record<number, string | null>
  >({});
  const [isLoadingDecryption, setIsLoadingDecryption] = useState(false);

  useEffect(() => {
    if (!accounts || accounts.length === 0 || !person?.estate_id) {
      return;
    }

    const decryptAccountNumbers = async () => {
      setIsLoadingDecryption(true);
      const decrypted: Record<number, string | null> = {};

      for (let i = 0; i < accounts.length; i++) {
        const account = accounts[i];
        if (account.account_number) {
          try {
            const decryptedValue = await decryptPersonData(
              person.estate_id,
              account.account_number,
              "account_number",
            );
            decrypted[i] = decryptedValue;
          } catch (error) {
            console.error(`Failed to decrypt account number at index ${i}:`, error);
            decrypted[i] = null;
          }
        } else {
          decrypted[i] = null;
        }
      }

      setDecryptedAccountNumbers(decrypted);
      setIsLoadingDecryption(false);
    };

    decryptAccountNumbers();
  }, [accounts, person?.estate_id]);

  if (!accounts || accounts.length === 0) {
    return (
      <div className="px-6 pb-6">
        <InsightsCard title="Investment Statement">
          <p className="text-sm text-stone-500">No account information found.</p>
        </InsightsCard>
      </div>
    );
  }

  return (
    <div className="px-6 pb-6 space-y-4">
      {/* Statement Overview */}
      {(institution_name || statement_date) && (
        <InsightsCard title="Statement Overview" noPadding>
          <InsightsDividerList>
            {institution_name && (
              <InsightsDividerRow
                label="Institution"
                value={institution_name}
                onClick={
                  getCitationForField(citations, "institution_name")
                    ? () => handleFieldClick("institution_name")
                    : undefined
                }
              />
            )}
            {statement_date && (
              <InsightsDividerRow
                label="Statement Date"
                value={formatDate(statement_date)}
                onClick={
                  getCitationForField(citations, "statement_date")
                    ? () => handleFieldClick("statement_date")
                    : undefined
                }
              />
            )}
            <InsightsDividerRow label="Total Accounts" value={accounts.length.toString()} />
          </InsightsDividerList>
        </InsightsCard>
      )}

      {/* Individual Accounts */}
      {accounts.map((account, index) => (
        <InsightsCard key={index} title={account.account_type || `Account ${index + 1}`} noPadding>
          <InsightsDividerList>
            <InsightsDividerRow
              label="Account Number"
              value={
                <MaskedAccountNumber
                  value={decryptedAccountNumbers[index]}
                  isLoading={isLoadingDecryption}
                />
              }
              onClick={
                getCitationForField(citations, `accounts.${index}.account_number`)
                  ? () => handleFieldClick(`accounts.${index}.account_number`)
                  : undefined
              }
            />
            {account.total_value !== undefined && (
              <InsightsDividerRow
                label="Total Value"
                value={formatCurrency(account.total_value)}
                onClick={
                  getCitationForField(citations, `accounts.${index}.total_value`)
                    ? () => handleFieldClick(`accounts.${index}.total_value`)
                    : undefined
                }
              />
            )}
            {account.joint_account_holders && account.joint_account_holders.length > 0 && (
              <InsightsDividerRow
                label="Joint Holders"
                value={account.joint_account_holders.join(", ")}
                onClick={
                  getCitationForField(citations, `accounts.${index}.joint_account_holders`)
                    ? () => handleFieldClick(`accounts.${index}.joint_account_holders`)
                    : undefined
                }
              />
            )}
            {account.tod_beneficiaries && account.tod_beneficiaries.length > 0 && (
              <InsightsDividerRow
                label="TOD Beneficiaries"
                value={account.tod_beneficiaries.join(", ")}
                onClick={
                  getCitationForField(citations, `accounts.${index}.tod_beneficiaries`)
                    ? () => handleFieldClick(`accounts.${index}.tod_beneficiaries`)
                    : undefined
                }
              />
            )}
          </InsightsDividerList>

          {/* Holdings */}
          {account.holdings && account.holdings.length > 0 && (
            <div className="border-t border-stone-100">
              <div className="py-2.5 px-4 bg-stone-50">
                <span className="text-sm font-medium text-stone-700">
                  Holdings ({account.holdings.length})
                </span>
              </div>
              <InsightsDividerList>
                {account.holdings.map((holding, holdingIndex) => {
                  const holdingCitationPath = `accounts.${index}.holdings.${holdingIndex}.company_name`;
                  const hasCitation = !!getCitationForField(citations, holdingCitationPath);
                  return (
                    <div
                      key={holdingIndex}
                      className={`py-3 px-4 border-b border-stone-100 last:border-b-0${hasCitation ? " cursor-pointer hover:bg-subtle transition-colors" : ""}`}
                      onClick={
                        hasCitation ? () => handleFieldClick(holdingCitationPath) : undefined
                      }
                    >
                      <div className="flex justify-between items-start mb-1">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-stone-900 truncate inline-flex items-center">
                            {holding.company_name || "Unknown Security"}
                            {holding.ticker && (
                              <span className="text-stone-500 ml-1">({holding.ticker})</span>
                            )}
                            {hasCitation && (
                              <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-green-300 shrink-0" />
                            )}
                          </p>
                          {holding.cusip_number && (
                            <p className="text-sm text-stone-500">CUSIP: {holding.cusip_number}</p>
                          )}
                        </div>
                        {holding.market_value !== undefined && (
                          <span className="text-sm font-medium text-stone-900 ml-4">
                            {formatCurrency(holding.market_value)}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-4 text-sm text-stone-500">
                        {holding.shares !== undefined && (
                          <span>{holding.shares.toLocaleString()} shares</span>
                        )}
                        {holding.cost_basis !== undefined && (
                          <span>Cost: {formatCurrency(holding.cost_basis)}</span>
                        )}
                        {holding.transfer_agent && <span>Agent: {holding.transfer_agent}</span>}
                      </div>
                    </div>
                  );
                })}
              </InsightsDividerList>
            </div>
          )}
        </InsightsCard>
      ))}
    </div>
  );
}
